import React, {useState} from "react";
import axios from "axios";
import {Modal, OverlayTrigger, Table, Tooltip} from "react-bootstrap";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import SelectList from "../SelectList.jsx";
import {friendlyErrorMessage} from "../SalesAnalysis/format.js";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";

const ART_OPTIONEN = [
    {value: "kunde", label: "Kunde"},
    {value: "artikel", label: "Artikel"},
    {value: "beleg", label: "Beleg"},
];

const GILT_FUER_OPTIONEN = [
    {value: "alle", label: "Alle Auswertungen"},
    {value: "unerfuellt", label: "Nur unerfüllte Nachfrage"},
];

const LEERE_FORM = {id: null, art: "kunde", wert: "", gilt_fuer: "alle", grund: "", aktiv: true};

/**
 * "Ausschlüsse von der Bedarfsrechnung" (PLAN.md Abschnitt 4a.1a): Tabelle
 * plus Anlegen/Bearbeiten in einem gemeinsamen Modal, Deaktivieren als
 * einfacher Umschalter (PUT aktiv=false) - kein natives Auswahlelement,
 * kein confirm().
 */
export default function AusschluesseTabelle({ausschluesse, canParameters, onGeaendert}) {
    const {addToast} = useExternalControls();
    const [form, setForm] = useState(null);
    const [speichert, setSpeichert] = useState(false);

    if (!ausschluesse) {
        return null;
    }

    const oeffnenNeu = () => setForm({...LEERE_FORM});
    const oeffnenBearbeiten = (a) => setForm({id: a.id, art: a.art, wert: a.wert, gilt_fuer: a.gilt_fuer, grund: a.grund || "", aktiv: a.aktiv});
    const schliessen = () => setForm(null);

    const speichern = () => {
        setSpeichert(true);
        const payload = {art: form.art, wert: form.wert, gilt_fuer: form.gilt_fuer, grund: form.grund, aktiv: form.aktiv};
        const request = form.id ? axios.put(`/cosmos/daten/ausschluesse/${form.id}`, payload) : axios.post("/cosmos/daten/ausschluesse", payload);

        request
            .then(() => {
                addToast(<FlexToast variant="success" title="Cosmos" autohide message="Ausschluss gespeichert – wirkt nach der nächsten Berechnung." />);
                schliessen();
                onGeaendert?.();
            })
            .catch((e) => {
                addToast(<FlexToast variant="danger" title="Cosmos" autohide={false} message={friendlyErrorMessage(e, "Der Ausschluss konnte nicht gespeichert werden.")} />);
            })
            .finally(() => setSpeichert(false));
    };

    const umschalten = (a) => {
        axios
            .put(`/cosmos/daten/ausschluesse/${a.id}`, {art: a.art, wert: a.wert, gilt_fuer: a.gilt_fuer, grund: a.grund, aktiv: !a.aktiv})
            .then(() => onGeaendert?.())
            .catch((e) => {
                addToast(<FlexToast variant="danger" title="Cosmos" autohide={false} message={friendlyErrorMessage(e, "Der Status konnte nicht geändert werden.")} />);
            });
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small text-muted">Lohnfertiger, Verrechnungskonten und ähnliche Sonderfälle – vor jeder Bedarfsrechnung ausgeschlossen.</span>
                {canParameters && (
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="left" overlay={<Tooltip id="cosmos-ausschluss-anlegen">Öffnet ein Formular, um einen neuen Ausschluss (Kunde, Artikel oder Beleg) anzulegen.</Tooltip>}>
                        <span>
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={oeffnenNeu}>
                                Ausschluss anlegen
                            </button>
                        </span>
                    </OverlayTrigger>
                )}
            </div>

            {ausschluesse.length > 0 && (
                <Table responsive size="sm" className="align-middle">
                    <thead>
                        <tr>
                            <th>
                                <HeaderWithHelp help="Ob der Ausschluss für einen Kunden, einen Artikel oder einen einzelnen Beleg gilt.">Art</HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Die ausgeschlossene Kunden-, Artikel- oder Belegnummer.">Wert</HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Ob der Ausschluss für alle Auswertungen gilt oder nur für die unerfüllte Nachfrage.">Gilt für</HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Warum dieser Ausschluss besteht.">Grund</HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Ob der Ausschluss gerade wirkt oder deaktiviert ist.">Status</HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Wer den Ausschluss angelegt hat.">Angelegt von</HeaderWithHelp>
                            </th>
                            {canParameters && <th />}
                        </tr>
                    </thead>
                    <tbody>
                        {ausschluesse.map((a) => (
                            <tr key={a.id} className={a.aktiv ? undefined : "text-muted"}>
                                <td className="text-capitalize">{a.art}</td>
                                <td>{a.wert}</td>
                                <td>{GILT_FUER_OPTIONEN.find((o) => o.value === a.gilt_fuer)?.label || a.gilt_fuer}</td>
                                <td className="small">{a.grund}</td>
                                <td>
                                    <span className={`badge ${a.aktiv ? "text-bg-success" : "text-bg-secondary"}`}>{a.aktiv ? "aktiv" : "deaktiviert"}</span>
                                </td>
                                <td className="small text-muted">{a.angelegt_von || "–"}</td>
                                {canParameters && (
                                    <td className="text-nowrap">
                                        <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-ausschluss-bearbeiten-${a.id}`}>Öffnet diesen Ausschluss zum Bearbeiten.</Tooltip>}>
                                            <span>
                                                <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => oeffnenBearbeiten(a)}>
                                                    Bearbeiten
                                                </button>
                                            </span>
                                        </OverlayTrigger>
                                        <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG}
                                            placement="top"
                                            overlay={<Tooltip id={`cosmos-ausschluss-umschalten-${a.id}`}>{a.aktiv ? "Deaktiviert den Ausschluss – er zählt danach wieder in der Bedarfsrechnung mit." : "Aktiviert den Ausschluss wieder."}</Tooltip>}
                                        >
                                            <span>
                                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => umschalten(a)}>
                                                    {a.aktiv ? "Deaktivieren" : "Aktivieren"}
                                                </button>
                                            </span>
                                        </OverlayTrigger>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <Modal show={Boolean(form)} onHide={schliessen} centered aria-labelledby="cosmos-ausschluss-title">
                <Modal.Header closeButton>
                    <Modal.Title id="cosmos-ausschluss-title" style={{fontSize: 16}}>
                        {form?.id ? "Ausschluss bearbeiten" : "Ausschluss anlegen"}
                    </Modal.Title>
                </Modal.Header>
                {form && (
                    <Modal.Body className="d-flex flex-column gap-2">
                        <div>
                            <HeaderWithHelp help="Ob der Ausschluss für einen Kunden, einen Artikel oder einen einzelnen Beleg gilt.">
                                <span className="form-label small mb-1 d-block">Art</span>
                            </HeaderWithHelp>
                            <SelectList value={form.art} options={ART_OPTIONEN} onChange={(v) => setForm({...form, art: v})} size="sm" ariaLabel="Art" />
                        </div>
                        <div>
                            <HeaderWithHelp help="Die auszuschließende Kunden-, Artikel- oder Belegnummer.">
                                <label className="form-label small mb-1" htmlFor="cosmos-ausschluss-wert">
                                    Wert (z. B. Kundennummer)
                                </label>
                            </HeaderWithHelp>
                            <input
                                id="cosmos-ausschluss-wert"
                                type="text"
                                className="form-control form-control-sm"
                                value={form.wert}
                                onChange={(e) => setForm({...form, wert: e.target.value})}
                            />
                        </div>
                        <div>
                            <HeaderWithHelp help="Alle Auswertungen: der Ausschluss gilt überall. Nur unerfüllte Nachfrage: er gilt ausschließlich dort.">
                                <span className="form-label small mb-1 d-block">Gilt für</span>
                            </HeaderWithHelp>
                            <SelectList value={form.gilt_fuer} options={GILT_FUER_OPTIONEN} onChange={(v) => setForm({...form, gilt_fuer: v})} size="sm" ariaLabel="Gilt für" />
                        </div>
                        <div>
                            <HeaderWithHelp help="Pflichtfeld: warum dieser Ausschluss besteht.">
                                <label className="form-label small mb-1" htmlFor="cosmos-ausschluss-grund">
                                    Grund (Pflichtfeld)
                                </label>
                            </HeaderWithHelp>
                            <textarea
                                id="cosmos-ausschluss-grund"
                                className="form-control form-control-sm"
                                rows={2}
                                value={form.grund}
                                onChange={(e) => setForm({...form, grund: e.target.value})}
                            />
                        </div>
                    </Modal.Body>
                )}
                <Modal.Footer>
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id="cosmos-ausschluss-abbrechen">Schließt das Formular, ohne zu speichern.</Tooltip>}>
                        <span>
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={schliessen}>
                                Abbrechen
                            </button>
                        </span>
                    </OverlayTrigger>
                    <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id="cosmos-ausschluss-speichern">Legt den Ausschluss an bzw. speichert die Änderung – wirkt nach der nächsten Berechnung.</Tooltip>}>
                        <span>
                            <button type="button" className="btn btn-primary btn-sm" disabled={speichert || !form?.wert || !form?.grund} onClick={speichern}>
                                Speichern
                            </button>
                        </span>
                    </OverlayTrigger>
                </Modal.Footer>
            </Modal>
        </>
    );
}
